import { useState, useEffect, useCallback } from "react";
import { Satellite, RefreshCw } from "lucide-react";
import { api } from "@/services/api";
import { Footer } from "@/components/Footer";
import { PageHeader } from "@/components/PageHeader";
import { ErrorState, LoadingState } from "@/components/States";
import { ISSMap } from "@/components/ISSMap";
import { ISSPositionDisplay } from "@/components/iss/ISSPositionDisplay";
import { ISSFloridaStatus } from "@/components/iss/ISSFloridaStatus";
import { ISSFacts } from "@/components/iss/ISSFacts";
import { NASASpotStation } from "@/components/iss/NASASpotStation";

const getStatusStyle = (status) => {
  if (status === "Over Florida!") return "text-emerald-400 bg-emerald-500/20 border-emerald-500/30";
  if (status === "Nearby") return "text-amber-400 bg-amber-500/20 border-amber-500/30";
  return "text-slate-400 bg-slate-500/20 border-slate-500/30";
};

export default function ISSPage() {
  const [iss, setIss] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchISS = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getISS();
      setIss(res);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchISS();
    const interval = setInterval(fetchISS, 10000);
    return () => clearInterval(interval);
  }, [fetchISS]);

  return (
    <div className="min-h-screen" data-testid="iss-page">
      <div className="hero-gradient fixed inset-0 pointer-events-none" />
      
      <div className="relative max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 md:px-8 py-8">
        <PageHeader 
          icon={Satellite}
          title="ISS Tracker"
          subtitle="Track the International Space Station in real-time"
        />

        {error ? (
          <ErrorState message={error} onRetry={fetchISS} />
        ) : (
          <div className="space-y-6">
            <div className="glass-card p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="section-title">Current Status</h2>
                {iss && (
                  <span className={`status-badge ${getStatusStyle(iss.status)}`}>
                    {iss.status}
                  </span>
                )}
              </div>

              {loading && !iss ? (
                <LoadingState message="Tracking ISS..." />
              ) : iss && (
                <>
                  <div className="mb-6">
                    <ISSMap 
                      latitude={iss.latitude} 
                      longitude={iss.longitude}
                      isOverFlorida={iss.is_over_florida}
                      isNearby={iss.is_nearby}
                    />
                  </div>

                  <ISSPositionDisplay latitude={iss.latitude} longitude={iss.longitude} />
                  <ISSFloridaStatus isOverFlorida={iss.is_over_florida} isNearby={iss.is_nearby} />

                  {lastUpdated && (
                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
                      <button
                        onClick={fetchISS}
                        className="flex items-center gap-1 text-cosmic-blue hover:text-cosmic-blue-glow"
                        style={{ transition: "color 0.2s ease" }}
                        data-testid="refresh-iss"
                      >
                        <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
                        Refresh
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            <NASASpotStation url={iss?.spot_the_station_url} />
            <ISSFacts />
          </div>
        )}

        <Footer />
      </div>
    </div>
  );
}
